import { AlertTriangle, ExternalLink } from "lucide-react";
import { runtimeMode } from "@/lib/runtimeMode";
import { Button } from "@/components/ui/button";

/**
 * Renders a full-screen blocking warning when the app is running as a
 * production build but Supabase env vars are not set.
 *
 * This prevents real garage staff from accidentally working in localStorage
 * demo mode after deployment (where data would be lost on browser clear).
 *
 * In development (`vite dev`) or when Supabase vars are present, renders null.
 */
export const ProductionGuard = () => {
  if (!runtimeMode.isProductionWithoutSupabase) return null;

  const missing = runtimeMode.missingSupabaseEnvVars;

  return (
    <div
      data-production-guard
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="production-guard-title"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background p-6 text-center"
    >
      <div className="max-w-md w-full space-y-6">
        <div className="h-16 w-16 rounded-2xl bg-destructive/15 text-destructive grid place-items-center mx-auto">
          <AlertTriangle className="h-8 w-8" />
        </div>

        <div>
          <h1 id="production-guard-title" className="text-2xl font-extrabold text-destructive">
            Production configuration missing
          </h1>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            This production build requires Supabase environment variables, but they
            are not set. The app cannot start without them — running in localStorage
            demo mode on a production deployment would risk data loss.
          </p>
        </div>

        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-start text-sm space-y-1.5">
          <p className="font-bold text-destructive">Missing variables:</p>
          {missing.map((v) => (
            <p key={v} className="font-mono text-destructive/80">{v}</p>
          ))}
        </div>

        <div className="text-sm text-muted-foreground space-y-1">
          <p>
            Set these environment variables in the Cloudflare Pages project, then redeploy.
          </p>
          <p>
            See <code className="text-xs bg-muted px-1 rounded">docs/env-vars.md</code> for
            setup instructions.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Retry after setting env vars
          </Button>
          <p className="text-xs text-muted-foreground">
            If you are a developer, run <code className="text-xs bg-muted px-1 rounded">npm run dev</code> instead —
            local demo mode is supported in development only.
          </p>
        </div>
      </div>
    </div>
  );
};
